import { PUBLIC_API_URL, PUBLIC_BRAND_CODE } from '$env/static/public';
import type { Cookies } from '@sveltejs/kit';
import {
	ReferralResponse,
	type AppDownloadLink,
	type BookData,
	type BundleData,
	type CategoryItem,
	type Chapter,
	type CheckoutData,
	type ComboData,
	type DokuPaymentData,
	type DraftBookChapter,
	type DraftBookData,
	type LoginDataResponse,
	type OrderData,
	type PaymentStatus,
	type ReviewData,
	type VerificationType
} from './types';
import { dev } from '$app/environment';
import { toast } from 'svelte-sonner';
import { goto, invalidateAll } from '$app/navigation';
import { priceFormatter } from './helpers/global';
import dayjs from 'dayjs';

/**
 * Returns the copyright year string.
 * If the current year is the same as CREATED_YEAR, only CREATED_YEAR is returned.
 * Otherwise, a string in the format of "CREATED_YEAR - currentYear" is returned.
 *
 * @returns {string} The copyright year string.
 */
function getCopyrightYear() {
	const CREATED_YEAR = 2024;
	const currentYear = new Date().getFullYear();

	if (CREATED_YEAR === currentYear) return CREATED_YEAR;
	return `${CREATED_YEAR} - ${currentYear}`;
}

/**
 * Sends a request to obtain an OTP for the specified username.
 *
 * Constructs a URL based on the provided username and verification type,
 * and sends a fetch request to retrieve the OTP. The URL endpoint varies
 * depending on the type of verification process.
 *
 * @param {string} username - The username for which the OTP is requested.
 * @param {VerificationType} [type] - The type of verification process.
 * Can be 'forgot-password', 'deactivate', or 'delete-account'.
 * Default is undefined, which uses the general OTP endpoint.
 *
 * @returns {Promise<{ meta: Pick<HTTPMetaResponse, 'status' | 'message'> } | null>}
 * A promise that resolves to an object containing meta information about the request,
 * or null if an error occurs during the request.
 */
async function requestOTP(username: string, type?: VerificationType) {
	let urlString = `/auth/otp/${PUBLIC_BRAND_CODE}/${username}`;

	switch (type) {
		case 'forgot-password':
			urlString = `/auth/forgot/${PUBLIC_BRAND_CODE}/${username}`;
			break;
		case 'deactivate':
			urlString = `/member/otp-deactivated/${PUBLIC_BRAND_CODE}/${username}`;
			break;
		case 'delete-account':
			urlString = `/member/otp-delete/${PUBLIC_BRAND_CODE}/${username}`;
			break;
	}

	const apiUrl = new URL(`${PUBLIC_API_URL}${urlString}`);

	try {
		const request = await fetch(apiUrl);
		const response = await request.json();

		type MetaResponse = Pick<HTTPMetaResponse, 'status' | 'message'>;

		return response as { meta: MetaResponse };
	} catch (error) {
		console.error(error);
		return null;
	}
}

/**
 * Sets login cookies for user_token, refresh_token, and identifier.
 * Each cookie is set with the following options:
 * - path: '/'
 * - httpOnly: true
 * - sameSite: 'lax'
 * - secure: true in production, false in dev
 * - maxAge:
 *   - user_token and identifier: 1 day
 *   - refresh_token: 7 days
 *
 * @param {Cookies} cookies
 * @param {LoginDataResponse['token']} tokenData
 */
function setLoginCookies(cookies: Cookies, tokenData: LoginDataResponse['token']) {
	const tokenObj = {
		user_token: tokenData.token,
		refresh_token: tokenData.refreshToken,
		identifier: tokenData.identifier
	};

	const tokenDataArray = Object.entries(tokenObj);
	for (const [key, value] of tokenDataArray) {
		cookies.set(key, value, {
			path: '/',
			httpOnly: true,
			sameSite: 'lax',
			secure: !dev,
			maxAge: key === 'refresh_token' ? 60 * 60 * 24 * 7 : 60 * 60 * 24
		});
	}
}

/**
 * Redirects to the login page and passes the current URL as a query parameter.
 * The query parameter is named 'redirect_url'.
 *
 * This function is used to redirect the user to the login page if the user is not logged in.
 * It is called by the handleNotLoggedIn function.
 */
function gotoLogin() {
	const loginUrl = new URL(`/login`, location.origin);

	loginUrl.searchParams.set('redirect_url', location.href);
	goto(loginUrl);
}

/**
 * Searches for books based on the given URL and optional parameters.
 *
 * @param {URL} url - The URL to get the search parameters from.
 * @param {string} [apiPath='/book-digital'] - The API path to use for the search.
 * @param {Record<string, string>} [alternateParams={}] - Alternate search parameters.
 *
 * @returns {Promise<{meta: HTTPMetaResponse, data: BookData[]}>} A promise that resolves to the search result.
 */
interface SearchBookParams {
	apiPath?: string;
	token?: string;
	alternateParams?: Record<string, string>;
	isNotUseDefaultParams?: boolean;
}
async function searchBooks(url: URL, args?: SearchBookParams) {
	const defaultParams = {
		page: args?.isNotUseDefaultParams ? '' : 1,
		limit: args?.isNotUseDefaultParams ? '' : 15,
		sort_by: args?.isNotUseDefaultParams ? '' : 'newest'
	};

	const params = {
		page: args?.alternateParams?.page || url.searchParams.get('page') || defaultParams.page,
		search: args?.alternateParams?.search || url.searchParams.get('query') || null,
		limit: args?.alternateParams?.limit || url.searchParams.get('per_page') || defaultParams.limit,
		is_free: args?.alternateParams?.is_free || url.searchParams.get('is_free') || null,
		sort_by:
			args?.alternateParams?.sort_by || url.searchParams.get('sort_by') || defaultParams.sort_by,
		min_price: args?.alternateParams?.min_price || url.searchParams.get('min_price') || null,
		max_price: args?.alternateParams?.max_price || url.searchParams.get('max_price') || null,
		category_id: args?.alternateParams?.category_id || url.searchParams.get('category_id') || null,
		publisher_id:
			args?.alternateParams?.publisher_id || url.searchParams.get('publisher_id') || null,
		writer_id: args?.alternateParams?.writer_id || url.searchParams.get('writer_id') || null,
		bundle_id: args?.alternateParams?.bundle_id || url.searchParams.get('bundle_id') || null,
		category_slug:
			args?.alternateParams?.category_slug || url.searchParams.get('category_slug') || null,
		is_published:
			args?.alternateParams?.is_published || url.searchParams.get('is_published') || null
	} as const;

	const apiUrl = new URL(`${PUBLIC_API_URL}${args?.apiPath ?? '/book-digital'}`);
	const paramsArray = Object.entries(params);

	for (const [key, value] of paramsArray) {
		if (value) apiUrl.searchParams.set(key, String(value));
		else apiUrl.searchParams.delete(key);
	}

	try {
		const headers: HeadersInit = {
			'Content-Type': 'application/json'
		};

		if (args?.token) headers.Authorization = `Bearer ${args?.token}`;

		const request = await fetch(apiUrl, {
			method: 'GET',
			headers
		});
		const response = await request.json();
		const {
			meta: { message }
		} = await response;

		if (request.ok) {
			const {
				meta,
				data
			}: {
				meta: HTTPMetaResponse;
				data: BookData[];
			} = response;

			return {
				meta,
				data
			};
		} else {
			console.error(message);
			return null;
		}
	} catch (error) {
		console.error(error);
		return null;
	}
}

/**
 * Gets a list of category items from the API.
 *
 * @param {string} path - The API path to use for the request.
 * @param {Record<string, string>} [params={}] - The query parameters to pass to the API.
 *
 * @returns {Promise<CategoryItem[]>} A promise that resolves to the list of category items.
 */
async function getCategoryItems(path: string, params: Record<string, string | number> = {}) {
	const apiUrl = new URL(`${PUBLIC_API_URL}${path}`);
	const paramsArray = Object.entries(params);

	for (const [key, value] of paramsArray) {
		if (value) apiUrl.searchParams.set(key, String(value));
		else apiUrl.searchParams.delete(key);
	}

	try {
		const request = await fetch(apiUrl);
		const response = await request.json();
		const {
			meta: { status, message }
		} = await response;

		if (request.ok) {
			return response as {
				meta: HTTPMetaResponse;
				data: CategoryItem[];
			};
		} else {
			console.error(status, message);
			return null;
		}
	} catch (error) {
		console.error(error);
		return null;
	}
}

/**
 * Adds a book to the user's cart.
 *
 * @param {string} userToken - The user's authentication token.
 * @param {{ id: number, subscriptionId: number }} args - The book ID and subscription ID.
 *
 * @returns {Promise<HTTPMetaResponse & { data: CartItem[] }>} A promise that resolves to the result of adding the book to cart.
 */
async function addToCart(
	userToken: string,
	{
		id,
		subscriptionId
	}: {
		id: number;
		subscriptionId: number;
	}
) {
	try {
		const request = await fetch(`${PUBLIC_API_URL}/cart/${PUBLIC_BRAND_CODE}`, {
			method: 'PUT',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${userToken}`
			},
			body: JSON.stringify({ book_id: id, subscription_plan_id: subscriptionId })
		});

		const response = await request.json();

		return response;
	} catch (error) {
		console.error(error);
		toast.error('Oops, terjadi kesalahan saat menambahkan buku ke keranjang');
	}
}

/**
 * Redirects to the checkout page for a specific book or bundle.
 *
 * Constructs a URL based on the type ('book' or 'bundle'), the item ID,
 * and the subscription ID, then navigates to the constructed URL.
 *
 * @param {('book' | 'bundle')} type - The type of item to checkout.
 * @param {Object} params - The parameters for checkout.
 * @param {number} params.id - The ID of the item to checkout.
 * @param {number} params.subscriptionId - The subscription ID associated with the item.
 */
function gotoCheckout(
	type: 'book' | 'bundle',
	{
		id,
		subscriptionId
	}: {
		id: number;
		subscriptionId: number;
	}
) {
	const checkoutUrl = new URL(`/checkout/${type}/${id}/${subscriptionId}`, location.origin);
	const currentUrl = location.href;

	checkoutUrl.searchParams.set('back_url', currentUrl);

	goto(checkoutUrl);
}

/**
 * Fetches book recommendations from the API.
 *
 * @param {string} query - Search query. If empty, returns top 10 books.
 * @returns {Promise<BookData[]>} - Array of book recommendations
 */
const getRecommendationBooks = async (query: string | null, params?: Record<string, string>) => {
	const apiUrl = new URL(`${PUBLIC_API_URL}/book-digital/similar-book`);

	if (query) apiUrl.searchParams.set('search', query);
	if (params) {
		for (const [key, value] of Object.entries(params)) {
			if (value) apiUrl.searchParams.set(key, value);
		}
	}

	try {
		const request = await fetch(apiUrl);
		const response = await request.json();
		const {
			meta: { status, message }
		} = await response;

		if (request.ok) {
			const {
				data
			}: {
				data: BookData[];
			} = response;

			return data;
		} else {
			console.error(status, message);
			return [];
		}
	} catch (error) {
		console.error(error);
		return [];
	}
};

/**
 * Converts a payment status code to a human-readable string.
 *
 * @param {PaymentStatus} status - The payment status code.
 * @returns {string} The human-readable text representation of the payment status.
 */
function paymentStatusHuman(status: PaymentStatus) {
	let text = '';

	switch (status) {
		case 'WAITING_FOR_PAYMENT':
			text = 'Pending';
			break;
		case 'PAYMENT_EXPIRED':
			text = 'Kedaluwarsa';
			break;
		case 'PAYMENT_FAILED':
			text = 'Gagal';
			break;
		case 'COMPLETED':
			text = 'Sukses';
			break;
		case 'LUNAS':
			text = 'Lunas';
			break;
		case 'CANCELED':
			text = 'Dibatalkan';
			break;
		case 'UPDATE_BUKTI_TRANSFER':
			text = 'Menunggu Verifikasi';
			break;
		case 'DP_COMPLETED':
			text = 'DP Sukses';
			break;
		default:
			text = status;
			break;
	}

	return text;
}

/**
 * Create a DOKU order.
 *
 * @param {string} orderId - The order ID.
 * @returns {Promise<DokuPaymentData | null>} The DOKU payment data if the request
 * is successful, otherwise `null`.
 */
async function createDokuOrder(orderId: string) {
	try {
		const request = await fetch(`${PUBLIC_API_URL}/doku/create-order/${orderId}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			}
		});

		const response = await request.json();
		const {
			meta: { status, message }
		}: {
			meta: HTTPMetaResponse;
		} = response;

		if (request.ok) {
			const {
				data
			}: {
				data: DokuPaymentData;
			} = response;

			return data;
		} else {
			console.error(status, message);
			toast.error(message);
		}
	} catch (error) {
		console.error(error);
		toast.error('Oops, terjadi kesalahan saat melakukan checkout');
	}
}

/**
 * Fetches a book by its identifier from the API.
 *
 * @param {string | null} userToken - The user's authentication token, or `null` if
 * the request should be made without authentication.
 * @param {string} identifier - The book identifier.
 * @returns {Promise<BookData | null>} The book data if the request is successful,
 * otherwise `null`.
 */
const getBook = async (userToken: string | null, identifier: string) => {
	const headers: HeadersInit = {
		'Content-Type': 'application/json'
	};

	if (userToken) headers.Authorization = `Bearer ${userToken}`;

	try {
		const request = await fetch(`${PUBLIC_API_URL}/book-digital/${identifier}`, {
			method: 'GET',
			headers
		});
		const response = await request.json();
		const {
			meta: { message }
		} = await response;

		if (request.ok) {
			const {
				data
			}: {
				data: BookData;
			} = response;

			return data;
		} else {
			console.error(message);
			return null;
		}
	} catch (error) {
		console.error(error);
		return null;
	}
};

/**
 * Fetches a bundle from the API.
 *
 * @param {string} id - The bundle ID.
 * @returns {Promise<BundleData | null>} The bundle data if the request is successful,
 * otherwise `null`.
 */
const getBundle = async (id: string) => {
	try {
		const request = await fetch(`${PUBLIC_API_URL}/premium-bundle/${id}`);
		const response = await request.json();
		const {
			meta: { status, message }
		} = await response;

		if (request.ok) {
			const {
				data
			}: {
				data: BundleData;
			} = response;

			return data;
		} else {
			console.error(status, message);
			return null;
		}
	} catch (error) {
		console.error(error);
		return null;
	}
};

/**
 * Fetches the reviews for a book from the API.
 *
 * @param {number} bookId - The book ID.
 * @param {URL} [url] - The URL to use for the request, which can include query
 * parameters.
 *
 * @returns {Promise<{ meta: HTTPMetaResponse; data: ReviewData[] } | null>}
 * The reviews data if the request is successful, otherwise `null`.
 */
const getReviews = async (bookId: number, alternateParams?: Record<string, string>, url?: URL) => {
	const apiUrl = new URL(`${PUBLIC_API_URL}/book-digital-rating/ratings/${bookId}`);

	if (url) {
		const params = url.searchParams;
		const alternateParamsArray = Object.entries(alternateParams || {});
		const mergedParams = new URLSearchParams([...params, ...alternateParamsArray]);
		const paramsArray = mergedParams.entries();

		for (const [key, value] of paramsArray) {
			if (value) apiUrl.searchParams.set(key, value);
		}
	}

	try {
		const request = await fetch(apiUrl);
		const response = await request.json();
		const {
			meta: { status, message }
		}: {
			meta: HTTPMetaResponse;
		} = await response;

		if (status === 200) {
			const {
				meta,
				data
			}: {
				meta: HTTPMetaResponse;
				data: ReviewData[];
			} = response;

			return {
				meta,
				data
			};
		} else {
			console.error(status, message);
			return null;
		}
	} catch (error) {
		console.error(error);
		return null;
	}
};

/**
 * Format a price value for display.
 *
 * @param {number | undefined} priceValue - The price value to format.
 * @returns {string} - The formatted price as a string. If the price is 0,
 *   returns the formatted currency string using `priceFormatter`. If the
 *   price is not a number, returns a dash ('-'). If the price is undefined,
 *   returns 'Gratis'.
 */
function formatPriceForDisplay(priceValue: number | undefined) {
	if (typeof priceValue === 'number') {
		if (priceValue === 0) return 'Gratis';

		return priceFormatter.format(priceValue);
	}

	return '-';
}

/**
 * Creates an order for a book or bundle.
 *
 * @param {('book' | 'bundle')} type - The type of the item to create an order for.
 * @param {string} userToken - The user's authentication token.
 * @param {Object} params - The parameters for creating the order.
 * @param {number} params.subscriptionId - The subscription ID associated with the order.
 * @param {boolean} params.isNormalPrice - Indicates if the normal price should be used.
 * @param {boolean} [params.referral=false] - Indicates if the order is a referral.
 * @param {number} [params.identifier] - The identifier for the book or bundle.
 *
 * @returns {Promise<string | undefined>} The order number if the request is successful.
 */
async function createOrder(
	type: 'book' | 'bundle',
	userToken: string,
	{
		subscriptionId,
		isNormalPrice,
		referral,
		identifier
	}: {
		subscriptionId: number;
		isNormalPrice: boolean;
		identifier?: number;
		referral?: string;
	}
) {
	let urlString = '';

	const body: {
		subscription_plan_id: number;
		normal_price: boolean;
		referral?: string;
		book_digital_id?: number;
		premium_bundle_id?: number;
	} = {
		subscription_plan_id: subscriptionId,
		normal_price: isNormalPrice
	};

	if (referral) body.referral = referral;

	switch (type) {
		case 'book':
			urlString = `${PUBLIC_API_URL}/member/payment/book-order`;
			body.book_digital_id = identifier;
			break;
		case 'bundle':
			urlString = `${PUBLIC_API_URL}/member/payment/book-order/subscription`;
			body.premium_bundle_id = identifier;
			break;
	}

	const url = new URL(urlString);

	try {
		const request = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${userToken}`
			},
			body: JSON.stringify(body)
		});

		const response = await request.json();
		const {
			meta: { status, message }
		}: {
			meta: HTTPMetaResponse;
		} = response;

		if (request.ok) {
			const {
				data: { order_no }
			}: {
				data: CheckoutData;
			} = response;

			return order_no;
		} else {
			console.error(status, message);
			toast.error(message);
		}
	} catch (error) {
		console.error(error);
		toast.error('Oops, terjadi kesalahan saat melakukan checkout');
	}
}

/**
 * Claims a free book that has been purchased.
 *
 * @param {string} userToken - The user's authentication token.
 * @param {string} orderNo - The order number of the book.
 *
 * @returns {Promise<void>} A promise that resolves when the book has been
 * successfully claimed, or rejects with an error message.
 */
async function claimFreeBook(userToken: string, orderNo: string) {
	try {
		const request = await fetch(`${PUBLIC_API_URL}/member/payment/free/book-claim`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${userToken}`
			},
			body: JSON.stringify({ order_no: orderNo })
		});

		const response = await request.json();
		const {
			meta: { status, message }
		}: {
			meta: HTTPMetaResponse;
		} = response;

		if (status === 200) {
			toast.success('Buku berhasil diklaim dan telah ditambahkan ke Perpustakaan Saya');
			invalidateAll();
		} else {
			console.error(status, message);
			toast.error(message);
		}
	} catch (error) {
		console.error(error);
		toast.error('Oops, terjadi kesalahan saat klaim buku gratis');
	}
}

/**
 * Fetches the list of categories from the API.
 *
 * @param {Record<string, string>} [params={}] - The query parameters to pass to
 * the API.
 *
 * @returns {Promise<CategoryItem[]>} A promise that resolves to the list of
 * categories if the request is successful, otherwise an empty array.
 */
async function getCategories(params: Record<string, string> = {}) {
	try {
		const apiUrl = new URL(`${PUBLIC_API_URL}/book-category`);

		for (const [key, value] of Object.entries(params)) {
			if (value) apiUrl.searchParams.set(key, value);
			else apiUrl.searchParams.delete(key);
		}

		const request = await fetch(apiUrl);
		const response = await request.json();
		const {
			meta: { status, message }
		} = await response;

		if (request.ok) {
			const {
				data
			}: {
				data: CategoryItem[];
			} = response;

			return data;
		} else {
			toast.error(message);
			console.error(status, message);
			return [];
		}
	} catch (error) {
		console.error(error);
		toast.error(`Oops, terjadi kesalahan saat memuat kategori`);
		return [];
	}
}

/**
 * Sets cookies for password creation session with a token and expiration time.
 *
 * This function sets two cookies:
 * - `create_pass_session`: Stores the provided token for session identification.
 * - `create_pass_expired_at`: Stores the expiration time of the session.
 *
 * Both cookies are set with the following options:
 * - path: '/create-password'
 * - sameSite: 'strict'
 * - secure: true in production, false in development
 * - httpOnly: true
 * - maxAge: 10 minutes for `create_pass_session`
 *
 * @param {Cookies} cookies - The Cookies object to set the session cookies.
 * @param {string} token - The session token to store in the `create_pass_session` cookie.
 */
function setCreatePassSession(cookies: Cookies, token: string) {
	const MAX_AGE = 60 * 10;
	const expiredAt = dayjs().add(MAX_AGE, 'seconds').toDate().getTime();

	cookies.set('create_pass_session', token, {
		path: '/create-password',
		sameSite: 'strict',
		secure: !dev,
		httpOnly: true,
		maxAge: MAX_AGE
	});

	cookies.set('create_pass_expired_at', String(expiredAt), {
		path: '/create-password',
		sameSite: 'strict',
		secure: !dev,
		httpOnly: true
	});
}

/**
 * Transform an array of ComboData into an array of SelectItem.
 *
 * @param {ComboData[]} data The array of ComboData to transform.
 * @return {SelectItem[]} An array of SelectItem.
 */
function transformToSelectItem(data: ComboData[]) {
	return data.map((item, id) => ({
		id,
		label: item.title || item.name || '-',
		value: String(item.id)
	}));
}

/**
 * Gets chosen category items from the API.
 *
 * @param {string} columnAPIId - The API ID of the column to get the chosen items from.
 * @param {number[]} values - The values of the chosen items.
 *
 * @returns {Promise<CategoryItem[]>} A promise that resolves to an array of CategoryItem.
 */
async function getChosenCategoryItems(columnAPIId: string, values: number[]) {
	const apiUrl = new URL(`${PUBLIC_API_URL}/${columnAPIId}/chosen/${values.join(',')}`);

	try {
		const request = await fetch(apiUrl);
		const response = await request.json();
		const {
			meta: { status, message }
		}: {
			meta: HTTPMetaResponse;
		} = response;

		if (status === 200) {
			const { data } = response;

			return data as CategoryItem[];
		} else {
			console.error(status, message);
			return [];
		}
	} catch (error) {
		console.error(error);
		toast.error('Gagal mengambil data pilihan');
		return [];
	}
}

/**
 * Fetches a draft book by its identifier from the API.
 *
 * @param {number} id - The book identifier.
 * @returns {Promise<DraftBookData | null>} The draft book data if the request is successful,
 * otherwise `null`.
 */
async function getDraftBook(id: number) {
	try {
		const request = await fetch(`${PUBLIC_API_URL}/book-draft/${id}`);
		const response = await request.json();
		const {
			meta: { status, message }
		}: {
			meta: HTTPMetaResponse;
		} = await response;

		if (status === 200) {
			const {
				data
			}: {
				data: DraftBookData;
			} = response;

			return data;
		} else {
			console.error(status, message);
			return null;
		}
	} catch (error) {
		console.error(error);
		return null;
	}
}

/**
 * Validates a referral code by sending a request to the API and returns the status and message.
 *
 * @async
 * @function checkVoucher
 * @param {string} referral - The referral code to validate.
 * @returns {Promise<{status: number, message: string}>}
 * A promise that resolves with an object containing:
 * - `status` {number}: HTTP status code from the API or `500` on error.
 * - `message` {string}: A message indicating the validation result.
 *
 * @throws {Error} If the fetch API is not supported or the request fails.
 *
 * @example
 * checkVoucher('ABC123')
 *   .then(response => console.log(response))
 *   .catch(error => console.error(error));
 */
async function checkVoucher(referral: string): Promise<{ status: number; message: string }> {
	try {
		if (!window.fetch) throw new Error('Browser tidak mendukung fetch API');

		const response = await fetch(`${PUBLIC_API_URL}/validation-referral/${referral}`);
		const result = await response.json();
		const {
			meta: { message, status }
		} = result;

		const responseMessages: Record<string, string> = {
			[ReferralResponse.Valid]: 'Kode Diskon valid',
			[ReferralResponse.Invalid]: 'Kode Diskon tidak valid',
			[ReferralResponse.Expired]: 'Kode Diskon telah kedaluwarsa',
			[ReferralResponse.InvalidDate]: 'Kode Diskon tidak bisa digunakan',
			[ReferralResponse.NotActivated]: 'Kode Diskon tidak bisa digunakan',
			[ReferralResponse.NotAccordance]: 'Kode Diskon tidak berlaku',
			[ReferralResponse.QuotaLimit]: 'Kuota Diskon telah melebihi batas'
		};

		return {
			status,
			message: responseMessages[message] || 'Status tidak dikenal'
		};
	} catch (error) {
		console.error(error);

		return {
			status: 500,
			message: error instanceof Error ? error.message : String(error)
		};
	}
}

/**
 * Merges two arrays of chapters into one, where each chapter
 * object has an additional `is_available` property.
 *
 * The `availableChapters` array is modified to include
 * `is_available: true`, and the `chapters` array is modified
 * to include `is_available: false` for chapters that are not
 * available.
 *
 * The two arrays are then merged into a single array and returned.
 *
 * @param {Chapter[]} chapters
 * @param {Chapter[]} availableChapters
 * @returns {DraftBookChapter[]}
 */
function getDraftBookChapters(
	chapters: Chapter[],
	availableChapters: Chapter[]
): DraftBookChapter[] {
	const modifiedChapters = availableChapters.map((chapter) => ({
		...chapter,
		is_available: chapter.has_order ? false : true
	}));

	const notAvailableChapters = chapters
		.filter((chapter) => {
			const isAvailable = modifiedChapters.some(
				(availableChapter) => availableChapter.id === chapter.id
			);

			return !isAvailable;
		})
		.map((chapter) => ({
			...chapter,
			is_available: false
		}));

	const mergedChapters = [...modifiedChapters, ...notAvailableChapters];
	return mergedChapters;
}

/**
 * Fetches a list of bundles from the API based on the given parameters.
 *
 * @param {Record<string, string>} params - An object containing query parameters.
 * @returns {Promise<{meta: HTTPMetaResponse, data: BundleData[]}>} - A promise that resolves to an object containing the meta response and the list of bundles.
 */
async function getBundles(params: Record<string, string>) {
	const apiUrl = new URL(`${PUBLIC_API_URL}/premium-bundle/list`);

	Object.entries(params).forEach(([key, value]) => {
		if (value) apiUrl.searchParams.set(key, value);
		else apiUrl.searchParams.delete(key);
	});

	try {
		const request = await fetch(apiUrl);
		const response = await request.json();
		const {
			meta
		}: {
			meta: HTTPMetaResponse;
		} = await response;

		const { status, message } = meta;

		if (status === 200) {
			const {
				data
			}: {
				data: BundleData[];
			} = response;

			return {
				meta,
				data
			};
		} else {
			console.error(status, message);
		}
	} catch (error) {
		console.error(error);
	}
}

type ParsedSections = Record<string, string[] | string>;

/**
 * Parse a string into sections, where sections are marked by a line that
 * starts and ends with '--', and the section name is the text in between.
 * The section name is converted to lowercase.
 *
 * If the section name is 'description', then the section value will be a
 * string of the lines that follow. Otherwise, the section value will be an
 * array of strings.
 *
 * @param {string} text The string to parse
 * @returns {ParsedSections} The parsed sections
 */
function parseSections(text: string): ParsedSections {
	const sections: ParsedSections = {};
	let currentKey: string | null = null;

	text.split(/\n/).forEach((line) => {
		const trimmed = line.trim();
		if (!trimmed) return;

		const match = trimmed.match(/^--(.*?)--$/);
		if (match) {
			currentKey = match[1].toLowerCase();
			sections[currentKey] = currentKey === 'description' ? '' : [];
		} else if (currentKey) {
			if (Array.isArray(sections[currentKey])) {
				(sections[currentKey] as string[]).push(trimmed);
			} else {
				sections[currentKey] += (sections[currentKey] ? ' ' : '') + trimmed;
			}
		}
	});

	return sections;
}

/**
 * Fetches order data from the API using the provided order number.
 *
 * @param {string} order_no - The order number to check.
 * @returns {Promise<OrderData | undefined>} The order data if the request is
 * successful, otherwise `undefined`.
 *
 * @throws Will throw an error if the API response status is not 200.
 */

async function getOrderData(order_no: string) {
	try {
		const request = await fetch(`${PUBLIC_API_URL}/check-order-no/${order_no}`);
		const response = await request.json();
		const {
			meta: { status, message }
		}: {
			meta: HTTPMetaResponse;
		} = response;

		if (status === 200) {
			const {
				data
			}: {
				data: OrderData;
			} = response;

			return data;
		}

		throw new Error(message);
	} catch (error) {
		console.error(error);
	}
}

/**
 * Fetches application download links from a remote API with caching.
 *
 * Makes a GET request to a specified endpoint to retrieve a list of app download links.
 * Caches the data in memory for 1 hour. If the cache is still valid, it returns the cached data.
 * Logs an error to the console if the request fails or if an error occurs during the fetch operation.
 *
 * @returns {Promise<AppDownloadLink[] | undefined>} A promise that resolves to an array of AppDownloadLink objects or undefined if an error occurs.
 */

async function getAppDownloadLinks() {
	const cache = new Map<string, { data: AppDownloadLink[]; expiry: number }>();
	const CACHE_KEY = 'appDownloadLinks';
	const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

	// Check if data is in cache and still valid
	const cached = cache.get(CACHE_KEY);
	if (cached && Date.now() < cached.expiry) {
		return cached.data;
	}

	try {
		const request = await fetch(`https://api.npoint.io/8c66b07b79c882fe761b`);

		if (request.ok) {
			const data = await request.json();

			// Cache the data with an expiry time
			cache.set(CACHE_KEY, { data, expiry: Date.now() + CACHE_DURATION });

			return data as AppDownloadLink[];
		}

		throw new Error(request.statusText);
	} catch (error) {
		console.error(error);
	}
}

export {
	getCopyrightYear,
	requestOTP,
	setLoginCookies,
	gotoLogin,
	searchBooks,
	getCategoryItems,
	addToCart,
	gotoCheckout,
	getRecommendationBooks,
	paymentStatusHuman,
	createDokuOrder,
	getBook,
	getBundle,
	getReviews,
	formatPriceForDisplay,
	createOrder,
	claimFreeBook,
	getCategories,
	setCreatePassSession,
	transformToSelectItem,
	getChosenCategoryItems,
	getDraftBook,
	checkVoucher,
	getDraftBookChapters,
	getBundles,
	parseSections,
	getOrderData,
	getAppDownloadLinks
};
